/**
 * @module funcao/label/funcaoediting
 */

import { Plugin } from 'ckeditor5/src/core';
import { DowncastWriter, Element } from 'ckeditor5/src/engine';
import { toWidget, viewToModelPositionOutsideModelElement, Widget } from 'ckeditor5/src/widget';
import FuncaoCommand from './funcaocommand';


const FUNCAO = 'funcao';

/**
 * The funcao editing.
 *
 * Introduces the `'funcao'` model element.
 *
 * @extends {Plugin}
 */
export default class FuncaoEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'FuncaoEditing';
	}

	static get requires() {
		return [ Widget ];
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );
	}

	/**
	 * @inheritDoc
	 */
	init() {
		this._defineSchema();
		this._defineConverters();


		this.editor.commands.add( FUNCAO, new FuncaoCommand( this.editor ) );

		this.editor.editing.mapper.on(
			'viewToModelPosition',
			viewToModelPositionOutsideModelElement( this.editor.model, viewElement => viewElement.hasClass( FUNCAO ) )
		);
	}

	/**
	 * @private
	 */
	_defineSchema() {
		const schema = this.editor.model.schema;
		schema.register( FUNCAO, {
			allowWhere: '$text',
			allowAttributesOf: '$text',
			allowAttributes: [ 'data' ],
			isInline: true,
			isObject: true
		} );
	}

	/**
	 * @private
	 */
	_defineConverters() {
		const conversion = this.editor.conversion;
		conversion.for( 'upcast' )
			.elementToElement( {
				view: {
					name: 'span',
					classes: [ FUNCAO ],
					attributes: [ 'data' ]
				},
				model: ( viewElement, { writer: modelWriter } ) => {
					const data = viewElement.getAttribute( 'data' );

					return modelWriter.createElement( FUNCAO, { data } );
				}
			} );

		conversion.for( 'editingDowncast' )
			.elementToElement( {
				model: FUNCAO,
				view: ( modelItem, { writer: viewWriter } ) => toWidget( createFuncaoViewElement( modelItem, viewWriter, true ), viewWriter )
			} );

		conversion.for( 'dataDowncast' )
			.elementToElement( {
				model: FUNCAO,
				view: ( modelItem, { writer: viewWriter } ) => createFuncaoViewElement( modelItem, viewWriter, false )
			} );
	}
}

/**
 * Creates a view element representing the funcao.
 *
 *	<span class="funcao"></span>
 *
 * @param {Element} modelItem
 * @param {DowncastWriter} viewWriter
 * @param {Boolean} [withLabel] Default false
 * @returns
 */
function createFuncaoViewElement( modelItem, viewWriter, withLabel = false ) {
	const funcaoName = modelItem.getAttribute( 'data' );

	const funcaoView = viewWriter.createContainerElement( 'span', {
		class: FUNCAO,
		data: funcaoName
	}, {
		isAllowedInsideAttributeElement: true
	} );

	if ( withLabel ) {
		const innerText = viewWriter.createText( funcaoName );
		viewWriter.insert( viewWriter.createPositionAt( funcaoView, 0 ), innerText );
	}

	return funcaoView;
}
